import React from 'react';
import { Globe, Menu, Star, ArrowRight } from 'lucide-react';
import { PageView } from '../types';
import BrandLogo from './BrandLogo';

interface NavbarProps {
    navigateTo: (page: PageView) => void;
    toggleLanguage: () => void;
    setIsLoginOpen: (open: boolean) => void;
    setIsSignUpOpen: (open: boolean) => void;
    t: any;
}

export default function Navbar({
    navigateTo,
    toggleLanguage,
    setIsLoginOpen,
    setIsSignUpOpen,
    t,
}: NavbarProps) {
    return (
        <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-4 px-4 pointer-events-none font-sans font-thin">
            <nav
                className="pointer-events-auto bg-white/80 backdrop-blur-2xl border border-black/[0.06] rounded-full shadow-lg shadow-black/[0.03] px-5 py-2.5 relative flex items-center transition-all duration-300 font-thin"
                style={{ maxWidth: '880px', width: '100%' }}
            >

                {/* Logo Section — Left */}
                <div className="flex items-center shrink-0">
                    <BrandLogo onClick={() => navigateTo('landing')} />
                </div>

                {/* Desktop Menu — True Center via absolute positioning */}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:flex items-center gap-1">
                    <button
                        onClick={() => navigateTo('landing')}
                        className="px-4 py-1.5 rounded-full text-sm font-medium text-brand-900/60 hover:text-brand-900 hover:bg-black/[0.04] transition-colors"
                    >
                        {t.nav.features}
                    </button>
                    <button
                        onClick={() => navigateTo('pricing')}
                        className="px-4 py-1.5 rounded-full text-sm font-medium text-brand-900/60 hover:text-brand-900 hover:bg-black/[0.04] transition-colors"
                    >
                        {t.nav.pricing}
                    </button>
                    <button
                        className="px-4 py-1.5 rounded-full text-sm font-medium text-brand-900/60 hover:text-brand-900 hover:bg-black/[0.04] transition-colors"
                    >
                        {t.nav.resources}
                    </button>
                </div>

                {/* Right Actions — pushed to the end */}
                <div className="flex-1 flex justify-end items-center gap-1.5">
                    <div className="hidden md:flex items-center gap-2">
                        {/* Language */}
                        <button
                            onClick={toggleLanguage}
                            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-black/[0.04] text-brand-900/40 hover:text-brand-900 transition-colors"
                        >
                            <Globe size={17} />
                        </button>

                        {/* Login — Ghost capsule */}
                        <button
                            onClick={() => setIsLoginOpen(true)}
                            className="px-4 py-2 text-sm font-medium text-brand-900/80 hover:text-brand-900 bg-black/[0.03] border border-black/[0.06] hover:bg-black/[0.05] rounded-full transition-all duration-200 flex items-center justify-center h-9"
                        >
                            {t.nav.login}
                        </button>

                        {/* Sign Up — Matte Natural Premium CTA */}
                        <button
                            onClick={() => setIsSignUpOpen(true)}
                            className="group relative overflow-hidden bg-brand-800 text-surface-light px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 active:scale-[0.97] flex items-center justify-center shadow-sm hover:shadow-md hover:-translate-y-0.5 border border-brand-900/10 hover:bg-brand-700 tracking-wide"
                        >
                            <span className="relative z-10 transition-transform duration-300 flex items-center gap-1.5">
                                {t.nav.signup}
                                <ArrowRight size={14} strokeWidth={2} className="group-hover:translate-x-1 transition-transform duration-300 opacity-80 group-hover:opacity-100" />
                            </span>
                        </button>
                    </div>

                    {/* Mobile Menu Button */}
                    <button className="md:hidden p-2 rounded-full hover:bg-black/5 text-brand-900">
                        <Menu className="w-5 h-5" />
                    </button>
                </div>
            </nav>
        </div>
    );
}
